import { useEffect, useMemo, useRef, useState } from 'react';
import { enrichWatchlistMapData } from '../utils/watchlistMapModel';

const MAX_GEOCODE_MS = 25000;

const clearMapState = (setMarkers, setRoutes, setBounds, setGeocoding) => {
  setMarkers((prev) => (prev.length === 0 ? prev : []));
  setRoutes((prev) => (prev.length === 0 ? prev : []));
  setBounds((prev) => (prev.length === 0 ? prev : []));
  setGeocoding((prev) => (prev === false ? prev : false));
};

/**
 * @param {Array} watchlistItems
 * @param {object|Array} optionsOrRiskAnalyses - legacy: riskAnalyses object; new: options bag
 * @param {number} legacyRefreshTrigger
 */
export function useWatchlistMapData(
  watchlistItems = [],
  optionsOrRiskAnalyses = {},
  legacyRefreshTrigger = 0
) {
  const options = useMemo(() => {
    if (Array.isArray(optionsOrRiskAnalyses) || typeof optionsOrRiskAnalyses !== 'object') {
      return { riskAnalyses: {}, routeAnalyses: {}, refreshTrigger: legacyRefreshTrigger };
    }
    if (
      'riskAnalyses' in optionsOrRiskAnalyses ||
      'routeAnalyses' in optionsOrRiskAnalyses ||
      'refreshTrigger' in optionsOrRiskAnalyses
    ) {
      return {
        riskAnalyses: optionsOrRiskAnalyses.riskAnalyses ?? {},
        routeAnalyses: optionsOrRiskAnalyses.routeAnalyses ?? {},
        refreshTrigger: optionsOrRiskAnalyses.refreshTrigger ?? legacyRefreshTrigger,
      };
    }
    return {
      riskAnalyses: optionsOrRiskAnalyses,
      routeAnalyses: {},
      refreshTrigger: legacyRefreshTrigger,
    };
  }, [optionsOrRiskAnalyses, legacyRefreshTrigger]);

  const [markers, setMarkers] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [bounds, setBounds] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  const [error, setError] = useState(null);

  const riskRef = useRef(options.riskAnalyses);
  const routeRef = useRef(options.routeAnalyses);
  riskRef.current = options.riskAnalyses;
  routeRef.current = options.routeAnalyses;

  const watchlistLen = watchlistItems?.length ?? 0;
  const routeAnalysisKey = useMemo(
    () => Object.keys(options.routeAnalyses || {}).sort().join(','),
    [options.routeAnalyses]
  );

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!watchlistLen) {
        clearMapState(setMarkers, setRoutes, setBounds, setGeocoding);
        return;
      }

      setGeocoding(true);
      setError(null);

      const geocodePromise = enrichWatchlistMapData(watchlistItems, {
        riskAnalyses: riskRef.current,
        routeAnalyses: routeRef.current,
      });
      const timeoutPromise = new Promise((resolve) => {
        setTimeout(() => resolve({ markers: [], routes: [], bounds: [], timedOut: true }), MAX_GEOCODE_MS);
      });

      try {
        const result = await Promise.race([geocodePromise, timeoutPromise]);
        if (!cancelled) {
          setMarkers(result.markers || []);
          setRoutes(result.routes || []);
          setBounds(result.bounds || []);
          if (result.timedOut) {
            setError(new Error('Map geocoding timed out'));
          }
        }
      } catch (err) {
        if (!cancelled) {
          setError(err);
          setMarkers([]);
          setRoutes([]);
          setBounds([]);
        }
      } finally {
        if (!cancelled) setGeocoding(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [watchlistItems, watchlistLen, options.refreshTrigger, routeAnalysisKey]);

  const hasMapContent = markers.length > 0 || routes.length > 0;

  return { markers, routes, bounds, geocoding, error, loading: geocoding, hasMapContent };
}
